import { useCallback } from 'react';
import { useIntl, type PrimitiveType } from 'react-intl';

type Values = Record<string, PrimitiveType>;

/**
 * Thin wrapper around `react-intl`'s `useIntl()` that mirrors the
 * `useTranslations(namespace?)` ergonomics the app was written against.
 * Returns a `t(key, values?)` function whose key is appended to the
 * optional namespace prefix; empty namespace means absolute key.
 *
 * The returned function MUST keep a stable identity: consumers put it in
 * dependency arrays (`useErrorDisplay` → `refetch` → `useEffect`), so a
 * fresh closure per render is an unbounded re-run waiting for a consumer
 * to close the cycle — see IMM-563 and BL-035. `namespace` is a string and
 * compares by value; `intl` is the only reference dependency, which is why
 * `I18nProvider` must pass a stable `onError` to `IntlProvider`.
 */
export function useTranslations(namespace?: string): (key: string, values?: Values) => string {
  const intl = useIntl();

  return useCallback(
    (key: string, values?: Values) => {
      const id = namespace ? `${namespace}.${key}` : key;

      return intl.formatMessage({ id }, values);
    },
    [intl, namespace],
  );
}
